/**
 * Generato automaticamente.
 * framework-monitoraggio-eventi
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  CreaEventoRequest,
  EventoDTO,
  GetEventiParams,
  WsRispostaPaginataListEventoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Cerca uno o più eventi
 * @summary Cerca eventi
 */
export const getEventi = (
  params?: GetEventiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListEventoDTO>(
    {
      url: `/framework-monitoraggio-eventi/eventi`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetEventiQueryKey = (params?: GetEventiParams) =>
  [
    `/framework-monitoraggio-eventi/eventi`,
    ...(params ? [params] : []),
  ] as const;

export const getGetEventiQueryOptions = <
  TData = Awaited<ReturnType<typeof getEventi>>,
  TError = ErrorType<unknown>,
>(
  params?: GetEventiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEventi>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getEventi>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEventiQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventi>>> = ({
    signal,
  }) => getEventi(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetEventiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEventi>>
>;
export type GetEventiQueryError = ErrorType<unknown>;

/**
 * @summary Cerca eventi
 */
export const useGetEventi = <
  TData = Awaited<ReturnType<typeof getEventi>>,
  TError = ErrorType<unknown>,
>(
  params?: GetEventiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEventi>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetEventiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un evento
 * @summary Crea evento
 */
export const postEventi = (
  creaEventoRequest: CreaEventoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<EventoDTO>(
    {
      url: `/framework-monitoraggio-eventi/eventi`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaEventoRequest,
    },
    options,
  );
};

export const getPostEventiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postEventi>>,
    TError,
    { data: CreaEventoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postEventi>>,
  TError,
  { data: CreaEventoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postEventi>>,
    { data: CreaEventoRequest }
  > = props => {
    const { data } = props ?? {};

    return postEventi(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostEventiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postEventi>>
>;
export type PostEventiMutationBody = CreaEventoRequest;
export type PostEventiMutationError = ErrorType<unknown>;

/**
 * @summary Crea evento
 */
export const usePostEventi = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postEventi>>,
    TError,
    { data: CreaEventoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostEventiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca uno o più eventi
 * @summary Dettaglio evento
 */
export const getEventiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<EventoDTO>(
    {
      url: `/framework-monitoraggio-eventi/eventi/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetEventiIdQueryKey = (id: string) =>
  [`/framework-monitoraggio-eventi/eventi/${id}`] as const;

export const getGetEventiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getEventiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEventiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getEventiId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEventiIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventiId>>> = ({
    signal,
  }) => getEventiId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetEventiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEventiId>>
>;
export type GetEventiIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio evento
 */
export const useGetEventiId = <
  TData = Awaited<ReturnType<typeof getEventiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEventiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetEventiIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
